/**
 * =============================================================================
 * Hook: useAffiliateBranding / useAffiliateBrandingPublic
 * Versão: 2.5.0
 *
 * CHANGELOG:
 *   v2.5.0 (2026-07-23) — Corrige "cache preso" na tela de login do afiliado.
 *     Adiciona `initialDataUpdatedAt: 0` + `refetchOnMount: 'always'` nos dois
 *     `useQuery`. Sem isso, o TanStack Query tratava o `initialData` (vindo
 *     de localStorage) como fresh a cada mount e nunca revalidava enquanto o
 *     staleTime de 5 min "reiniciava". Consequência: navegadores que
 *     cacheavam branding vazio (visita anterior ao deploy de p_tenant_slug
 *     ou antes do tenant salvar as cores) ficavam presos no default
 *     roxo/violeta. Agora hidrata sem flash e revalida em background.
 *   v1.0.0 — Versão inicial (Fase Branding por Tenant).
 *
 * Descrição:
 *   Carrega a identidade visual personalizada do painel do afiliado:
 *     - Logo da empresa (URL pública)
 *     - Cores HSL do gradiente (primary + accent)
 *     - Textos da tela de login (título, subtítulo, bullets, rodapé)
 *
 *   - `useAffiliateBranding(tenantId)`: para usuários autenticados
 *      (afiliados logados ou admin do tenant fazendo preview).
 *      Usa a RPC pública também (ela não exige auth e devolve só
 *      dados não-sensíveis).
 *   - `useAffiliateBrandingPublic({ tenantId, programSlug, tenantSlug })`:
 *      usado na tela de login do afiliado (sem auth) para resolver branding
 *      via querystring `?tenant=` / `?programa=` ou URL canônica
 *      `/afiliado/:tenantSlug/...`.
 *
 *   Sem branding configurado, devolve `null` em cada campo — o consumidor
 *   aplica os defaults indigo/violeta do design system.
 * =============================================================================
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('AffiliateBranding');

// -----------------------------------------------------------------------------
// Cache local de branding (evita flash da cor padrão no primeiro render).
//
// Estratégia: persistimos o último branding conhecido para o tenant em
// localStorage. Na próxima visita, hidratamos o React Query via `initialData`,
// fazendo com que `data` já venha preenchido de forma SÍNCRONA — antes do
// primeiro paint — eliminando o flash do roxo padrão definido no index.css.
// A RPC continua rodando em background para revalidar o cache.
// -----------------------------------------------------------------------------
const BRANDING_CACHE_PREFIX = 'aff:branding:';
// Prefixo separado para o hook PÚBLICO (tela de login/cadastro sem auth).
// Indexado por slug do tenant / slug do programa / tenantId — o que estiver
// disponível primeiro na URL. Garante hidratação síncrona antes do 1º paint
// e elimina o flash da cor primária padrão (roxa) na tela de login.
const BRANDING_PUBLIC_CACHE_PREFIX = 'aff:branding-public:';

function brandingCacheKey(tenantId: string | null | undefined): string | null {
  if (!tenantId) return null;
  return `${BRANDING_CACHE_PREFIX}${tenantId}`;
}

/**
 * Monta a chave de cache do hook público a partir dos parâmetros recebidos.
 * Prioriza `tenantSlug` (URL canônica `/afiliado/:tenantSlug/...`), depois
 * `programSlug` (`?programa=`), depois `tenantId` (`?tenant=`).
 */
function publicBrandingCacheKey(params: {
  tenantId?: string | null;
  programSlug?: string | null;
  tenantSlug?: string | null;
}): string | null {
  if (params.tenantSlug) return `${BRANDING_PUBLIC_CACHE_PREFIX}slug:${params.tenantSlug}`;
  if (params.programSlug) return `${BRANDING_PUBLIC_CACHE_PREFIX}prog:${params.programSlug}`;
  if (params.tenantId) return `${BRANDING_PUBLIC_CACHE_PREFIX}tid:${params.tenantId}`;
  return null;
}

/** Lê branding cacheado a partir de uma chave já montada. */
function readBrandingCacheByKey(
  key: string | null,
): AffiliateBrandingPayload | undefined {
  if (typeof window === 'undefined' || !key) return undefined;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    return JSON.parse(raw) as AffiliateBrandingPayload;
  } catch {
    return undefined;
  }
}

/** Persiste branding em uma chave de cache arbitrária. Silencia falhas. */
function writeBrandingCacheByKey(
  key: string | null,
  payload: AffiliateBrandingPayload,
): void {
  if (typeof window === 'undefined' || !key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignora — quota cheia ou storage indisponível
  }
}

/** Lê branding cacheado para um tenant (ou null se ausente/SSR/erro). */
function readBrandingCache(
  tenantId: string | null | undefined,
): AffiliateBrandingPayload | undefined {
  return readBrandingCacheByKey(brandingCacheKey(tenantId));
}

/** Persiste branding atualizado para o tenant. Silencia falhas (quota/SSR). */
function writeBrandingCache(
  tenantId: string | null | undefined,
  payload: AffiliateBrandingPayload,
): void {
  writeBrandingCacheByKey(brandingCacheKey(tenantId), payload);
}

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------
export interface AffiliateBrandingLoginBullet {
  /** Identificador do ícone (mapeado para componentes lucide). */
  icon: 'link' | 'chart' | 'wallet' | 'check' | 'sparkles';
  /** Texto exibido ao lado do ícone. */
  text: string;
}

export interface AffiliateBrandingLogin {
  title: string | null;
  subtitle: string | null;
  bullets: AffiliateBrandingLoginBullet[];
  footer_text: string | null;
}

export interface AffiliateBrandingVisual {
  /** URL pública da logo (Supabase Storage, bucket "affiliate"). */
  logo_url: string | null;
  /** Cor primária em formato HSL "H S% L%" (ex.: "258 81% 25%"). */
  primary_hsl: string | null;
  /** Cor secundária em formato HSL "H S% L%". */
  accent_hsl: string | null;
}

export interface AffiliateBrandingPayload {
  branding: AffiliateBrandingVisual | null;
  login: AffiliateBrandingLogin | null;
  tenant_name: string | null;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function normalize(raw: any): AffiliateBrandingPayload {
  const branding = raw?.branding ?? null;
  const login = raw?.login ?? null;
  return {
    branding: branding
      ? {
          logo_url: branding.logo_url ?? null,
          primary_hsl: branding.primary_hsl ?? null,
          accent_hsl: branding.accent_hsl ?? null,
        }
      : null,
    login: login
      ? {
          title: login.title ?? null,
          subtitle: login.subtitle ?? null,
          bullets: Array.isArray(login.bullets)
            ? login.bullets
                .filter((b: any) => b && typeof b.text === 'string')
                .slice(0, 4)
                .map((b: any) => ({
                  icon: (b.icon ?? 'sparkles') as AffiliateBrandingLoginBullet['icon'],
                  text: String(b.text).slice(0, 200),
                }))
            : [],
          footer_text: login.footer_text ?? null,
        }
      : null,
    tenant_name: raw?.tenant_name ?? null,
  };
}

// -----------------------------------------------------------------------------
// Hook autenticado (afiliado logado / admin do tenant)
// -----------------------------------------------------------------------------
export function useAffiliateBranding(tenantId: string | null | undefined) {
  // Hidratação síncrona via cache local — elimina o flash da cor padrão
  // no primeiro render quando já existe branding conhecido para o tenant.
  const cached = readBrandingCache(tenantId);

  const query = useQuery({
    queryKey: ['affiliate-branding', tenantId ?? null],
    enabled: true,
    staleTime: 5 * 60 * 1000,
    // `initialData` faz `data` já vir preenchido na 1ª renderização (evita flash).
    initialData: cached,
    // `initialDataUpdatedAt: 0` marca o cache como stale — assim a query
    // sempre revalida em background no mount, mesmo quando havia dado local.
    initialDataUpdatedAt: 0,
    refetchOnMount: 'always',

    queryFn: async (): Promise<AffiliateBrandingPayload> => {
      const { data, error } = await supabase.rpc(
        'get_affiliate_program_branding_public',
        { p_tenant_id: tenantId ?? null, p_program_slug: null } as any,
      );
      if (error) {
        log.error('Falha ao carregar branding', error);
        throw error;
      }
      return normalize(data);
    },
  });

  // Persiste o último branding conhecido para hidratar a próxima sessão.
  useEffect(() => {
    if (query.data && tenantId) {
      writeBrandingCache(tenantId, query.data);
    }
  }, [query.data, tenantId]);

  return query;
}

// -----------------------------------------------------------------------------
// Hook público (tela de login, sem auth)
// -----------------------------------------------------------------------------
export interface UseAffiliateBrandingPublicParams {
  tenantId?: string | null;
  programSlug?: string | null;
  /** NOVO: resolve branding pelo slug do tenant (URL canônica /afiliado/:tenantSlug/...) */
  tenantSlug?: string | null;
}

export function useAffiliateBrandingPublic(params: UseAffiliateBrandingPublicParams) {
  const { tenantId, programSlug, tenantSlug } = params;

  // Hidratação síncrona via cache local — elimina o flash da cor primária
  // padrão (roxa) na tela de login do afiliado quando já existe branding
  // conhecido para o tenant/programa nesta máquina. A RPC continua rodando
  // em background para revalidar.
  const cacheKey = publicBrandingCacheKey({ tenantId, programSlug, tenantSlug });
  const cached = readBrandingCacheByKey(cacheKey);

  const query = useQuery({
    queryKey: [
      'affiliate-branding-public',
      tenantId ?? null,
      programSlug ?? null,
      tenantSlug ?? null,
    ],
    enabled: !!(tenantId || programSlug || tenantSlug),
    staleTime: 5 * 60 * 1000,
    initialData: cached,
    // Marca o cache local como stale para forçar revalidação em background
    // no mount — evita ficar preso em um branding vazio salvo anteriormente.
    initialDataUpdatedAt: 0,
    refetchOnMount: 'always',

    queryFn: async (): Promise<AffiliateBrandingPayload> => {
      const { data, error } = await supabase.rpc(
        'get_affiliate_program_branding_public',
        {
          p_tenant_id: tenantId ?? null,
          p_program_slug: programSlug ?? null,
          p_tenant_slug: tenantSlug ?? null,
        } as any,
      );
      if (error) {
        log.error('Falha ao carregar branding público', error);
        throw error;
      }
      return normalize(data);
    },
  });

  // Persiste o último branding público conhecido para hidratar a próxima visita.
  useEffect(() => {
    if (query.data && cacheKey) {
      writeBrandingCacheByKey(cacheKey, query.data);
    }
  }, [query.data, cacheKey]);

  return query;
}

// -----------------------------------------------------------------------------
// Helper: monta CSS vars a aplicar no container .affiliate-shell
// -----------------------------------------------------------------------------
export function buildBrandingCssVars(
  branding: AffiliateBrandingVisual | null | undefined,
): React.CSSProperties {
  const style: Record<string, string> = {};
  if (!branding) return style;
  if (branding.primary_hsl) {
    style['--aff-primary'] = branding.primary_hsl;
  }
  if (branding.accent_hsl) {
    style['--aff-accent'] = branding.accent_hsl;
  }
  if (branding.primary_hsl || branding.accent_hsl) {
    const p = branding.primary_hsl ?? '258 81% 25%';
    const a = branding.accent_hsl ?? '173 77% 57%';
    style['--aff-gradient-hero'] =
      `linear-gradient(135deg, hsl(${p}) 0%, hsl(${a}) 100%)`;
  }
  return style as React.CSSProperties;
}
